import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Type, Image, Square, List, User, Grid, MessageSquare, MapPin, Mail, Settings,
  ChevronDown, Monitor, Smartphone, Save, Eye, Globe, X, Move, Trash2, Copy,
  ArrowUp, ArrowDown, Menu, Plus, Edit3
} from 'lucide-react';
import SalonPreview from './SalonPreview';
import ThemeSettingsModal from './ThemeSettingsModal';
import SEOSettingsModal from './SEOSettingsModal';
import EditBlockModal from './EditBlockModal';
import FloatingContactForm from './FloatingContactForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ELEMENT_TYPES = [
  { id: 'text', label: 'Text', icon: Type },
  { id: 'image', label: 'Image', icon: Image },
  { id: 'button', label: 'Button', icon: Square },
  { id: 'services', label: 'Service List', icon: List },
  { id: 'staff', label: 'Staff Profile', icon: User },
  { id: 'gallery', label: 'Gallery', icon: Grid },
  { id: 'testimonials', label: 'Testimonials', icon: MessageSquare },
  { id: 'map', label: 'Map', icon: MapPin },
  { id: 'contact_form', label: 'Contact Form', icon: Mail },
];

const PAGES = [
  { key: 'home', label: 'Home' },
  { key: 'services', label: 'Services' },
  { key: 'staff', label: 'Staff' },
  { key: 'gallery', label: 'Gallery' },
  { key: 'about', label: 'About' },
  { key: 'contact', label: 'Contact' },
];

const THEMES = [
  { key: 'Modern', label: 'Modern' },
  { key: 'Classic', label: 'Classic' },
  { key: 'Minimal', label: 'Minimal' },
];

export default function WebsiteBuilder() {
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('home');
  const [currentTheme, setCurrentTheme] = useState('Modern');
  const [viewMode, setViewMode] = useState('desktop');
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [showThemeSettings, setShowThemeSettings] = useState(false);
  const [showSEOSettings, setShowSEOSettings] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [showContactPanel, setShowContactPanel] = useState(true);
  const [draggedElement, setDraggedElement] = useState(null);

  // Data state
  const [pages, setPages] = useState([]);
  const [theme, setTheme] = useState(null);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [contactSettings, setContactSettings] = useState(null);
  const [mapSettings, setMapSettings] = useState(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [pagesRes, themeRes, servicesRes, staffRes, testimonialsRes, galleryRes, contactRes, mapRes] = await Promise.all([
        axios.get(`${API}/website/pages`),
        axios.get(`${API}/website/theme`),
        axios.get(`${API}/website/services`),
        axios.get(`${API}/website/staff`),
        axios.get(`${API}/website/testimonials`),
        axios.get(`${API}/website/gallery`),
        axios.get(`${API}/website/contact-form-settings`),
        axios.get(`${API}/website/map-settings`),
      ]);

      setPages(pagesRes.data);
      setTheme(themeRes.data);
      setCurrentTheme(themeRes.data.theme_name);
      setServices(servicesRes.data);
      setStaff(staffRes.data);
      setTestimonials(testimonialsRes.data);
      setGallery(galleryRes.data);
      setContactSettings(contactRes.data);
      setMapSettings(mapRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load website data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCurrentPageData = () => {
    return pages.find(p => p.page_key === currentPage) || null;
  };

  // Save current page
  const handleSave = async () => {
    const pageData = getCurrentPageData();
    if (!pageData) return;

    try {
      await axios.put(`${API}/website/pages/${currentPage}`, {
        blocks: pageData.blocks
      });
      toast.success('Draft saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save draft');
    }
  };

  // Preview page
  const handlePreview = () => {
    window.open('/preview', '_blank');
  };

  // Publish all
  const handlePublish = async () => {
    try {
      await axios.post(`${API}/website/publish-all`);
      toast.success('Website published successfully!');
      fetchData();
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error('Failed to publish');
    }
  };

  // Add block to page
  const handleAddBlock = (blockType) => {
    const pageData = getCurrentPageData();
    if (!pageData) return;

    const newBlock = {
      block_id: `block_${Date.now()}`,
      block_type: blockType,
      order_index: pageData.blocks.length,
      visible: true,
      content: getDefaultContent(blockType),
      styles: {},
      settings: {}
    };

    const updatedBlocks = [...pageData.blocks, newBlock];
    updatePageBlocks(updatedBlocks);
    toast.success(`Added ${blockType} block`);
  };

  const getDefaultContent = (blockType) => {
    switch (blockType) {
      case 'text':
        return { text: 'Enter your text here' };
      case 'image':
        return { url: '', alt: '' };
      case 'button':
        return { text: 'Click Here', link: '#' };
      case 'services':
        return { section_title: 'Our Services' };
      case 'staff':
        return { section_title: 'Meet Our Team' };
      case 'gallery':
        return { section_title: 'Gallery' };
      case 'testimonials':
        return { section_title: 'What Our Clients Say' };
      case 'map':
        return { title: 'Visit Us' };
      case 'contact_form':
        return { title: 'Contact Us' };
      case 'hero':
        return {
          heading: 'Luxe Salon',
          subheading: 'Style & Beauty Redefined',
          cta_text: 'Book Appointment',
          cta_link: '/booking',
          background_image: ''
        };
      case 'header':
        return {
          salon_name: theme?.salon_name || 'Luxe Salon',
          nav_items: ['Home', 'Services', 'Staff', 'About', 'Blog']
        };
      default:
        return {};
    }
  };

  const updatePageBlocks = async (blocks) => {
    const pageIndex = pages.findIndex(p => p.page_key === currentPage);
    if (pageIndex === -1) return;

    const updatedPages = [...pages];
    updatedPages[pageIndex] = { ...updatedPages[pageIndex], blocks };
    setPages(updatedPages);

    // Auto-save
    try {
      await axios.put(`${API}/website/pages/${currentPage}`, { blocks });
    } catch (error) {
      console.error('Error auto-saving:', error);
    }
  };

  // Block actions
  const handleEditBlock = (block) => {
    setEditingBlock(block);
    setShowEditModal(true);
  };

  const handleDeleteBlock = (blockId) => {
    const pageData = getCurrentPageData();
    if (!pageData) return;

    const updatedBlocks = pageData.blocks.filter(b => b.block_id !== blockId);
    updatedBlocks.forEach((b, i) => b.order_index = i);
    updatePageBlocks(updatedBlocks);
    setSelectedBlock(null);
    toast.success('Block deleted');
  };

  const handleDuplicateBlock = (block) => {
    const pageData = getCurrentPageData();
    if (!pageData) return;

    const newBlock = {
      ...block,
      block_id: `block_${Date.now()}`,
      order_index: block.order_index + 1
    };

    const updatedBlocks = [...pageData.blocks];
    updatedBlocks.splice(block.order_index + 1, 0, newBlock);
    updatedBlocks.forEach((b, i) => b.order_index = i);
    updatePageBlocks(updatedBlocks);
    toast.success('Block duplicated');
  };

  const handleMoveBlock = (blockId, direction) => {
    const pageData = getCurrentPageData();
    if (!pageData) return;

    const blockIndex = pageData.blocks.findIndex(b => b.block_id === blockId);
    if (blockIndex === -1) return;

    const newIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
    if (newIndex < 0 || newIndex >= pageData.blocks.length) return;

    const updatedBlocks = [...pageData.blocks];
    [updatedBlocks[blockIndex], updatedBlocks[newIndex]] = [updatedBlocks[newIndex], updatedBlocks[blockIndex]];
    updatedBlocks.forEach((b, i) => b.order_index = i);
    updatePageBlocks(updatedBlocks);
  };

  const handleBlockUpdate = (blockId, updates) => {
    const pageData = getCurrentPageData();
    if (!pageData) return;

    const updatedBlocks = pageData.blocks.map(b =>
      b.block_id === blockId ? { ...b, ...updates } : b
    );
    updatePageBlocks(updatedBlocks);
  };

  const handleInlineEdit = (blockId, field, value) => {
    const pageData = getCurrentPageData();
    if (!pageData) return;

    const updatedBlocks = pageData.blocks.map(b => {
      if (b.block_id === blockId) {
        return {
          ...b,
          content: { ...b.content, [field]: value }
        };
      }
      return b;
    });
    updatePageBlocks(updatedBlocks);
  };

  // Drag handlers
  const handleDragStart = (e, elementType) => {
    setDraggedElement(elementType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragEnd = () => {
    setDraggedElement(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedElement) {
      handleAddBlock(draggedElement);
    }
    setDraggedElement(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Theme update
  const handleThemeUpdate = async (updates) => {
    try {
      const res = await axios.put(`${API}/website/theme`, updates);
      setTheme(res.data);
      toast.success('Theme updated');
    } catch (error) {
      console.error('Error updating theme:', error);
      toast.error('Failed to update theme');
    }
  };

  if (loading) {
    return (
      <div className="website-builder">
        <div className="loading-spinner">Loading Website Builder...</div>
      </div>
    );
  }

  const pageData = getCurrentPageData();

  // Mobile gate for website builder
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  if (isMobile) {
    return (
      <div className="builder-mobile-gate" data-testid="builder-mobile-gate">
        <Monitor size={56} />
        <h2>Desktop Required</h2>
        <p>The Website Builder is optimized for desktop editing. Please use a desktop or tablet for the best experience.</p>
        <a href="/dashboard" className="builder-mobile-back">Back to Dashboard</a>
      </div>
    );
  }

  return (
    <div className="website-builder" data-testid="website-builder">
      {/* Top Toolbar */}
      <div className="editor-toolbar" data-testid="editor-toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">Hair Salon Website Builder Editor</span>
        </div>
        
        <div className="toolbar-center">
          <div className="toolbar-select">
            <span>Page:</span>
            <select 
              value={currentPage} 
              onChange={(e) => setCurrentPage(e.target.value)}
              data-testid="page-selector"
            >
              {PAGES.map(p => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
            <ChevronDown size={14} />
          </div>
          
          <div className="toolbar-select">
            <span>Theme:</span>
            <select 
              value={currentTheme}
              onChange={(e) => setCurrentTheme(e.target.value)}
              data-testid="theme-selector"
            >
              {THEMES.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            <ChevronDown size={14} />
          </div>
        </div>
        
        <div className="toolbar-right">
          <button 
            className="toolbar-btn"
            onClick={() => setShowSEOSettings(true)}
            data-testid="seo-settings-btn"
          >
            <Settings size={16} />
            SEO Settings
          </button>
          
          <button 
            className="toolbar-btn"
            onClick={() => setViewMode(viewMode === 'desktop' ? 'mobile' : 'desktop')}
            data-testid="mobile-preview-btn"
          >
            {viewMode === 'desktop' ? <Monitor size={16} /> : <Smartphone size={16} />}
            {viewMode === 'desktop' ? 'Mobile Preview' : 'Desktop Preview'}
          </button>
          
          <button className="toolbar-btn" onClick={handleSave} data-testid="save-btn">
            <Save size={16} />
            Save
          </button>
          
          <button className="toolbar-btn" onClick={handlePreview} data-testid="preview-btn">
            <Eye size={16} />
            Preview
          </button>
          
          <button className="toolbar-btn primary" onClick={handlePublish} data-testid="publish-btn">
            <Globe size={16} />
            Publish
          </button>
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="editor-main">
        {/* Left Sidebar - Add Elements */}
        <div className="editor-sidebar" data-testid="editor-sidebar">
          <div className="sidebar-header">Add Elements</div>
          <div className="sidebar-elements">
            {ELEMENT_TYPES.map(el => (
              <button
                key={el.id}
                className="element-btn"
                draggable
                onDragStart={(e) => handleDragStart(e, el.id)}
                onDragEnd={handleDragEnd}
                onClick={() => handleAddBlock(el.id)}
                data-testid={`add-${el.id}-btn`}
              >
                <el.icon size={18} />
                {el.label}
              </button>
            ))}
          </div>
          <button 
            className="theme-settings-btn"
            onClick={() => setShowThemeSettings(true)}
            data-testid="theme-settings-btn"
          >
            <Settings size={18} />
            Theme Settings
          </button>
        </div>

        {/* Canvas Area */}
        <div 
          className="editor-canvas"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          data-testid="editor-canvas"
        >
          <div className={`canvas-viewport ${viewMode}`}>
            {pageData && (
              <SalonPreview
                page={pageData}
                theme={theme}
                services={services}
                staff={staff}
                testimonials={testimonials}
                gallery={gallery}
                contactSettings={contactSettings}
                mapSettings={mapSettings}
                selectedBlock={selectedBlock}
                onSelectBlock={setSelectedBlock}
                onEditBlock={handleEditBlock}
                onDeleteBlock={handleDeleteBlock}
                onDuplicateBlock={handleDuplicateBlock}
                onMoveBlock={handleMoveBlock}
                onInlineEdit={handleInlineEdit}
                isEditing={true}
              />
            )}
            
            {draggedElement && (
              <div className="dropzone active">
                Drop here to add {draggedElement}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Contact Form Panel */}
      {showContactPanel && (
        <FloatingContactForm
          settings={contactSettings}
          onClose={() => setShowContactPanel(false)}
          onEdit={() => {
            setEditingBlock({ block_type: 'contact_form', content: contactSettings });
            setShowEditModal(true);
          }}
        />
      )}

      {/* Theme Settings Modal */}
      {showThemeSettings && (
        <ThemeSettingsModal
          theme={theme}
          onClose={() => setShowThemeSettings(false)}
          onSave={handleThemeUpdate}
        />
      )}

      {/* SEO Settings Modal */}
      {showSEOSettings && (
        <SEOSettingsModal
          page={pageData}
          onClose={() => setShowSEOSettings(false)}
          onSave={async (updates) => {
            try {
              await axios.put(`${API}/website/pages/${currentPage}`, updates);
              fetchData();
              toast.success('SEO settings saved');
            } catch (error) {
              toast.error('Failed to save SEO settings');
            }
          }}
        />
      )}

      {/* Edit Block Modal */}
      {showEditModal && editingBlock && (
        <EditBlockModal
          block={editingBlock}
          services={services}
          staff={staff}
          testimonials={testimonials}
          gallery={gallery}
          theme={theme}
          onClose={() => {
            setShowEditModal(false);
            setEditingBlock(null);
          }}
          onSave={(updates) => {
            if (editingBlock.block_id) {
              handleBlockUpdate(editingBlock.block_id, updates);
            }
            setShowEditModal(false);
            setEditingBlock(null);
          }}
          onUpdateServices={setServices}
          onUpdateStaff={setStaff}
          onUpdateTestimonials={setTestimonials}
          onUpdateGallery={setGallery}
          fetchData={fetchData}
        />
      )}
    </div>
  );
}
